/**
 * Hat renk ataması: route_color, yoksa hat kimliğinden deterministik palet.
 */

/** Açık ve koyu tabanda ayrışan palet; seçili durak vurgusu (SELECTED_STOP_COLOR) içermez */
export const ROUTE_PALETTE = Object.freeze([
  '#2563eb',
  '#16a34a',
  '#ea580c',
  '#9333ea',
  '#0891b2',
  '#ca8a04',
  '#dc2626',
  '#4f46e5',
  '#92400e',
  '#0f766e',
  '#65a30d',
  '#c026d3',
])

/** Seçili durak — başka hiçbir katmanda kullanılmaz */
export const SELECTED_STOP_COLOR = '#ff6b8b'

const HEX6 = /^#?([0-9a-f]{6})$/i
const HEX3 = /^#?([0-9a-f]{3})$/i

function relativeLuminance(hex) {
  const n = parseInt(hex.slice(1), 16)
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2]
}

/**
 * GTFS route_color → '#rrggbb'. Geçersiz ya da haritada görünmeyecek kadar açıksa null.
 */
export function normalizeRouteColor(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return null
  let hex = null
  const m6 = s.match(HEX6)
  if (m6) hex = `#${m6[1].toLowerCase()}`
  else {
    const m3 = s.match(HEX3)
    if (m3) {
      hex = `#${m3[1]
        .split('')
        .map((c) => c + c)
        .join('')
        .toLowerCase()}`
    }
  }
  if (!hex) return null
  if (relativeLuminance(hex) > 0.82) return null
  return hex
}

function hashString(str) {
  let h = 0x811c9dc5
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

export function paletteIndexForRoute(routeId) {
  return hashString(String(routeId ?? '')) % ROUTE_PALETTE.length
}

function routeLabel(route) {
  const short = String(route?.route_short_name ?? '').trim()
  const long = String(route?.route_long_name ?? '').trim()
  return short || long || String(route?.route_id ?? '')
}

/**
 * routes → Map<route_id, { color, short_name, long_name, label }>
 * Renksiz hatlar karma başlangıçlı yoklama ile paletten benzersiz renk alır
 * (palet dolana kadar çakışma olmaz; aynı feed hep aynı sonucu verir).
 */
export function buildRouteStyleIndex(routes) {
  const index = new Map()
  const pending = []
  for (const route of routes || []) {
    const rid = route?.route_id
    if (rid == null || String(rid).trim() === '') continue
    const id = String(rid)
    const color = normalizeRouteColor(route.route_color)
    index.set(id, {
      color,
      short_name: String(route.route_short_name ?? '').trim(),
      long_name: String(route.route_long_name ?? '').trim(),
      label: routeLabel(route),
    })
    if (!color) pending.push(id)
  }
  pending.sort((a, b) => a.localeCompare(b, 'tr', { numeric: true }))
  const used = new Set()
  for (const id of pending) {
    let idx = paletteIndexForRoute(id)
    if (used.size < ROUTE_PALETTE.length) {
      while (used.has(idx)) idx = (idx + 1) % ROUTE_PALETTE.length
    }
    used.add(idx)
    index.get(id).color = ROUTE_PALETTE[idx]
  }
  return index
}

export function routeColorFrom(index, routeId) {
  const hit = index?.get(String(routeId ?? ''))
  return hit?.color || ROUTE_PALETTE[paletteIndexForRoute(routeId)]
}
